import { commonService } from "../../../../utils/commonService";
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import { apiCall } from "../../../../utils/axiosService";
import { API_URL } from "../../../../utils/apiRoutes";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import Loader from "../../../Loader/Loader";
import { Button } from "react-bootstrap";
import CustomRangePicker from "../../../CommonComponent/CustomRangePIcker";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const GroupIQ = ({
  api,
  para,
  setting,
}) => {
  const [summaryData, setSummaryData] = useState([]);
  const [chartDataPayload, setChartDataPayload] = useState([]);
  const [loading, setLoading] = useState(false);
 
  const asOfDate = para?.asOfDate || moment().format("YYYY-MM-DD");
  const selectedDate = asOfDate;
  
  const [localStartDate, setLocalStartDate] = useState(selectedDate);
  const [localEndDate, setLocalEndDate] = useState(moment(selectedDate).add(14, 'days').format('YYYY-MM-DD'));
  const [selectedCardDate, setSelectedCardDate] = useState(selectedDate);

  const [startRangeDate, setStartRangeDate] = useState(moment(localStartDate).toDate());
  const [endRangeDate, setEndRangeDate] = useState(moment(localEndDate).toDate());

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [activePresetRange, setActivePresetRange] = useState(null);

  const handleDateChange = (event) => {
    const [start, end] = event;
    setStartRangeDate(start);
    setEndRangeDate(end);
    setActivePresetRange(null);
  };

  const pastAsOfDate = moment(asOfDate).subtract(19, "days").format("YYYY-MM-DD");
  const currentAsOfDate = asOfDate;
  const propertyCode =  para?.propertyCode || para?.PropertyCode || para?.propertycode || "";

  const normalize = (res) => {
    if (!res || res?.name === "AxiosError") return [];
    if (Array.isArray(res)) return res;
    if (res?.data && Array.isArray(res.data)) return res.data;
    if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
    return [];
  };

  const fetchSummaryData = async () => {
    if (api === 0 || !propertyCode) return;
    setLoading(true);
    
    try {
      const summaryResponse = await apiCall({
        method: "POST",
        url: API_URL.BASE_API_URL.SnapShot + API_URL.GROUP_IQ.WIDGET,
        body: {
          asofdate: currentAsOfDate,
          startdate: localStartDate,
          enddate: localEndDate,
          propertycode: propertyCode,
        },
      }, false);

      const washResponse = await apiCall({
        method: "POST",
        url: API_URL.BASE_API_URL.SnapShot + API_URL.GROUP_WASH.GROUP_WASH_OTB,
        body: {
          asofdate: currentAsOfDate,
          startdate: localStartDate,
          enddate: localEndDate,
          propertycode: propertyCode,
        },
      }, false);

      let normSummary = normalize(summaryResponse);
      let normWash = normalize(washResponse);

      if (normWash.length > 0) {
        const receivedDates = new Set(
          normWash
            .map((x) => x?.date)
            .filter(Boolean)
            .map((d) => moment(d).format("YYYY-MM-DD"))
        );
        normSummary = normSummary.filter((item) => {
          const formattedItem = moment(item.date || item.asofdate).format("YYYY-MM-DD");
          return receivedDates.has(formattedItem);
        });
      } else {
        normSummary = [];
      }

      setSummaryData(normSummary);
    } catch (error) {
      
    }
    setLoading(false);
  };

  const fetchChartData = async () => {
    if (api === 0 || !propertyCode || !selectedCardDate) return;
    const chartResponse = await apiCall({
      method: "POST",
      url: API_URL.BASE_API_URL.SnapShot + API_URL.GROUP_IQ.CHART,
      body: {
        selecteddate: selectedCardDate,
        pastasofdate: pastAsOfDate,
        currentasofdate: currentAsOfDate,
        propertycode: propertyCode,
      },
    }, false);
    
    setChartDataPayload(normalize(chartResponse));
  };

  useEffect(() => {
    fetchSummaryData();
  }, [api, propertyCode, localStartDate, localEndDate, currentAsOfDate]);

  useEffect(() => {
    fetchChartData();
  }, [api, propertyCode, selectedCardDate, pastAsOfDate, currentAsOfDate]);

  // Calculations for summary cards
  const totals = useMemo(() => {
    let groupBlock = 0;
    let groupConfirm = 0;
    let groupWash = 0;
    let blockChange = 0;
    let confirmChange = 0;

    const dataToProcess = selectedCardDate 
      ? summaryData.filter((d) => moment(d.date || d.asofdate).format("YYYY-MM-DD") === moment(selectedCardDate).format("YYYY-MM-DD"))
      : summaryData;

    const activeData = dataToProcess.length > 0 ? dataToProcess : summaryData;

    activeData.forEach((day) => {
      groupBlock += Number(day.groupblock || 0);
      groupConfirm += Number(day.groupconfirm || 0);
      blockChange += Number(day.groupblockchange || 0);
      confirmChange += Number(day.groupconfirmchange || 0);
      
      const wash = Number(day.groupblock || 0) - Number(day.groupconfirm || 0);
      groupWash += wash > 0 ? wash : 0;
    });

    const pickupRatio = groupBlock > 0 ? ((groupConfirm / groupBlock) * 100).toFixed(0) : 0;
    const washRatio = groupBlock > 0 ? ((groupWash / groupBlock) * 100).toFixed(0) : 0;

    return {
      groupBlock,
      groupConfirm,
      groupWash,
      pickupRatio,
      washRatio,
      blockChange,
      confirmChange,
    };
  }, [summaryData, selectedCardDate]);

  const getColorByWash = (day) => {
    if (day.colorcode) {
      const code = day.colorcode.toLowerCase();
      if (code === 'green') return "#22c55e";
      if (code === 'amber' || code === 'yellow') return "#eab308";
      if (code === 'orange') return "#f97316";
      if (code === 'red') return "#ef4444";
    }
    const washPct = day["groupwash%"] || day.groupwashpct || day.groupwash || 0;
    const p = parseFloat(String(washPct).replace('%', '')) || 0;
    if (p <= 30) return "#22c55e"; // Green
    if (p <= 60) return "#eab308"; // Yellow
    if (p <= 75) return "#f97316"; // Orange
    return "#ef4444"; // Red
  };


  // Chart configs
  const pickupCurveData = useMemo(() => {
    const labels = chartDataPayload.map((d) => d.dba);
    const block = chartDataPayload.map((d) => Number(d.groupblock || 0));
    const confirm = chartDataPayload.map((d) => Number(d.groupconfirm || 0));
    const tmpwash = chartDataPayload.map((d) => Number(d.groupblock || 0) - Number(d.groupconfirm || 0));



    return {
      labels,
      datasets: [
        {
          label: "Group Block",
          data: block,
          borderColor: "#f59e0b",
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 0,
        },
        {
          label: "Group Confirmed",
          data: confirm,
          borderColor: "#10b981",
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 0,
        },
        {
          label: "Group Wash",
          data: tmpwash,
          borderColor: "#ef4444",
          borderDash: [5, 5],
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          fill: true,
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 0,
        },
      ],
    };
  }, [chartDataPayload]);

  const pickupCurveOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, boxWidth: 8 },
      },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: "#f3f4f6" }, beginAtZero: true },
    },
  };

  const washStackData = useMemo(() => {
    const labels = chartDataPayload.map((d) => d.dba);
    const confirmPct = chartDataPayload.map((d) => {
      const b = Number(d.groupblock || 0);
      const c = Number(d.groupconfirm || 0);
      return b > 0 ? (c / b) * 100 : 0;
    });
    const washPct = chartDataPayload.map((d) => {
      const b = Number(d.groupblock || 0);
      const c = Number(d.groupconfirm || 0);
      const w = b - c;
      return b > 0 && w > 0 ? (w / b) * 100 : 0;
    });

    return {
      labels,
      datasets: [
        {
          label: "Group Confirmed %",
          data: confirmPct,
          backgroundColor: "#10b981",
          barPercentage: 0.8,
          categoryPercentage: 0.9,
        },
        {
          label: "Group Wash %",
          data: washPct,
          backgroundColor: "#ef4444",
          barPercentage: 0.8,
          categoryPercentage: 0.9,
        },
      ],
    };
  }, [chartDataPayload]);

  const washStackOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, boxWidth: 20 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(0)}%`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        ticks: { callback: (val) => val + "%" },
        grid: { color: "#f3f4f6" },
      },
    },
  };

  return (
    <div className="groupiq-container">
      {/* Header section */}
      <div className="groupiq-header">
        <div className="groupiq-header-left">
          <h5 className="groupiq-title">
            Group Arrival Window <span className="groupiq-title-subtitle">- {summaryData?.length > 0 ? summaryData?.length : 0 } days from</span>
          </h5>
          <div className="groupiq-date-controls">
            <div className="groupiq-date-input">
              <DatePicker
                id={commonService.generateGlobalClickTrackerID("GroupIQ", "range-date-picker", propertyCode)}
                portalId="datepicker-root"
                className="groupiq-datepicker"
                onChange={handleDateChange}
                startDate={startRangeDate}
                endDate={endRangeDate}
                selectsRange
                monthsShown={2}
                open={openDatePicker}
                onClickOutside={() => setOpenDatePicker(false)}
                onInputClick={() => setOpenDatePicker(true)}
                shouldCloseOnSelect={false}
                placeholderText="MM/DD/YYYY - MM/DD/YYYY"
              >
                <div className="w-100" style={{ display: "inline-block" }}>
                  <CustomRangePicker
                    id={commonService.generateGlobalClickTrackerID("GroupIQ", "occ-date-rangepicker", propertyCode)}
                    activePreset={activePresetRange}
                    setActivePreset={setActivePresetRange}
                    setOpenDatePicker={setOpenDatePicker}
                    onChange={(start, end, type) => {
                      setStartRangeDate(start);
                      setEndRangeDate(end);
                    }}
                  />
                  <div className="content-inside-calendar">
                    <p>
                      {startRangeDate ? commonService.getDateInFormat(startRangeDate) : ""}
                      {startRangeDate && endRangeDate ? " - " : ""}
                      {endRangeDate ? commonService.getDateInFormat(endRangeDate) : ""}
                    </p>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        id={commonService.generateGlobalClickTrackerID("GroupIQ", "occ-date-cancel-btn", propertyCode)}
                        className="secondary-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStartRangeDate(moment(localStartDate).toDate());
                          setEndRangeDate(moment(localEndDate).toDate());
                          setOpenDatePicker(false);
                          setActivePresetRange(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        id={commonService.generateGlobalClickTrackerID("GroupIQ", "occ-date-apply-btn", propertyCode)}
                        className="primary-btn"
                        disabled={!startRangeDate || !endRangeDate}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (startRangeDate && endRangeDate) {
                            setLocalStartDate(moment(startRangeDate).format("YYYY-MM-DD"));
                            setLocalEndDate(moment(endRangeDate).format("YYYY-MM-DD"));
                            setSelectedCardDate(moment(startRangeDate).format("YYYY-MM-DD"));
                            setOpenDatePicker(false);
                          }
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </DatePicker>
            </div>
            <Button id={commonService.generateGlobalClickTrackerID("GroupIQ", "reset", propertyCode)}
              className="default-btn primary-btn"
                onClick={() => {
                  setLocalStartDate(selectedDate);
                  setLocalEndDate(moment(selectedDate).add(14, 'days').format('YYYY-MM-DD'));
                  setSelectedCardDate(selectedDate);
                  setStartRangeDate(moment(selectedDate).toDate());
                  setEndRangeDate(moment(selectedDate).add(14, 'days').toDate());
                }}
                >
                    RESET
                </Button>
            {/* <button 
              onClick={() => {
                setLocalStartDate(selectedDate);
                setSelectedCardDate(selectedDate);
              }}
              className="groupiq-today-btn"
            >
             RESET
            </button> */}
          </div>
        </div>
        
        <div className="groupiq-legend">
          <span className="groupiq-legend-item"><span className="groupiq-legend-color groupiq-legend-clear"></span> CLEAR ≤30%</span>
          <span className="groupiq-legend-item"><span className="groupiq-legend-color groupiq-legend-watch"></span> WATCH 30-60%</span>
          <span className="groupiq-legend-item"><span className="groupiq-legend-color groupiq-legend-warn"></span> WARN 60-75%</span>
          <span className="groupiq-legend-item"><span className="groupiq-legend-color groupiq-legend-critical"></span> CRITICAL >75%</span>
        </div>
      </div>

      {loading ? (
        <div className="groupiq-loading">     
            <Loader />
          </div>
      ) : (
        <>
          {/* Scrollable Date Cards */}
          <div className="groupiq-date-cards-container">
            {summaryData.map((day, idx) => {
              const dateStr = day.date || day.asofdate;
              const mDate = moment(dateStr);
              const washPct = day["groupwash%"] || day.groupwashpct || day.groupwash || 0;
              const p = parseFloat(String(washPct).replace('%', '')) || 0;
              const color = getColorByWash(day);
              const formattedDateStr = moment(dateStr).format("YYYY-MM-DD");
              const isSelected = formattedDateStr === moment(selectedCardDate).format("YYYY-MM-DD");
              
              return (
                <div id={commonService.generateGlobalClickTrackerID("GroupIQ", "group-wash-dba", day)} 
                  key={idx} 
                  onClick={() => setSelectedCardDate(formattedDateStr)}
                  className={`groupiq-date-card ${isSelected ? 'selected' : ''}`}
                    style={{
                      "--accent": color,
                      "--selected": isSelected ? color : 'transparent',
                    }}
                >
                  <div className="groupiq-date-card-day">{mDate.format("ddd")}</div>
                  <div className="groupiq-date-card-date">{mDate.format("DD")}</div>
                  <div className="groupiq-date-card-month">{mDate.format("MMM")}</div>
                  
                  <div className="groupiq-date-card-progress">
                    <div className="groupiq-date-card-progress-bar" style={{ "--w": `${p}%`, "--accent": color }}></div>
                  </div>
                  
                  <div className="groupiq-date-card-labels">
                    <span>GROUP</span>
                    <span className="groupiq-percent" style={{ "--accent": color }}>{p.toFixed(0)}%</span>
                  </div>
                  <div className="groupiq-date-card-wash-label">
                    WASH
                  </div>
                  
                  <div className="groupiq-date-card-footer">
                    DBA {day.leadtime || day.dba || 0}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 4 Summary Cards */}
          <div className="groupiq-summary-cards">
            <div className="groupiq-summary-card groupiq-card-block">
              <div className="groupiq-summary-card-label">GROUP BLOCK</div>
              <div className="groupiq-summary-card-value">
                {totals.groupBlock} <span className="groupiq-summary-card-unit">rms</span>
              </div>
              <div className="groupiq-summary-card-change">
                <span className="groupiq-summary-card-change-badge groupiq-badge-green">↑</span>
                <span className="groupiq-summary-card-change-text">Δ {totals.blockChange > 0 ? `+${totals.blockChange}` : totals.blockChange} last 7d</span>
              </div>
            </div>

            <div className="groupiq-summary-card groupiq-card-confirmed">
              <div className="groupiq-summary-card-label">GROUP CONFIRMED</div>
              <div className="groupiq-summary-card-value">
                {totals.groupConfirm} <span className="groupiq-summary-card-unit">rms</span>
              </div>
              <div className="groupiq-summary-card-change">
                <span className="groupiq-summary-card-change-badge groupiq-badge-green">↑</span>
                <span className="groupiq-summary-card-change-text">Δ {totals.confirmChange > 0 ? `+${totals.confirmChange}` : totals.confirmChange} last 7d</span>
              </div>
            </div>

            <div className="groupiq-summary-card groupiq-card-wash">
              <div className="groupiq-summary-card-label">GROUP WASH</div>
              <div className="groupiq-summary-card-value">
                {totals.groupWash} <span className="groupiq-summary-card-unit">rms</span>
              </div>
              <div className="groupiq-summary-card-change">
                <span className="groupiq-summary-card-change-badge groupiq-badge-red">↑</span>
                <span className="groupiq-summary-card-change-text">{totals.washRatio}% of group block · {totals.washRatio > 75 ? "Critical" : totals.washRatio > 60 ? "Warn" : totals.washRatio > 30 ? "Watch" : "Clear"}</span>
              </div>
            </div>

            <div className="groupiq-summary-card groupiq-card-ratio">
              <div className="groupiq-summary-card-label">GROUP PICKUP RATIO</div>
              <div className="groupiq-summary-card-value">
                {totals.pickupRatio}<span className="groupiq-percent-large">%</span>
              </div>
              <div className="groupiq-summary-card-change">
                <span className="groupiq-summary-card-change-badge groupiq-badge-green">↑</span>
                <span className="groupiq-summary-card-change-text">{totals.groupConfirm}/{totals.groupBlock} converted</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="groupiq-charts-row">
            {/* Pickup Curve */}
            <div className="groupiq-chart-container">
              <div className="groupiq-chart-header">
                <div>
                  <h4 className="groupiq-chart-title">Group Pickup Curve</h4>
                  <div className="groupiq-chart-subtitle">
                    Group Block · Group Confirmed · Group Wash Across Days-Before-Arrival
                  </div>
                </div>
                <div className="groupiq-chart-range">
                  DBA -19 → -1
                </div>
              </div>
              <div className="groupiq-chart-wrapper">
                <Line data={pickupCurveData} options={pickupCurveOptions} />
              </div>
            </div>

            {/* Wash Stack */}
            <div className="groupiq-chart-container">
              <div className="groupiq-chart-header">
                <div>
                  <h4 className="groupiq-chart-title">Group Wash Stack</h4>
                  <div className="groupiq-chart-subtitle">
                    Group Confirmed Share vs Group Wash Share, Day By Day
                  </div>
                </div>
                <div className="groupiq-chart-range">
                  % of Group Block
                </div>
              </div>
              <div className="groupiq-chart-wrapper">
                <Bar data={washStackData} options={washStackOptions} />
              </div>
            </div>
          </div>

          {/* Group Daily Snapshot Grid */}
          <div className="groupiq-snapshot-container">
            <div className="groupiq-snapshot-header">
              <h4 className="groupiq-snapshot-title">Group Daily Snapshot</h4>
              <div className="groupiq-snapshot-count">
                {chartDataPayload.length} SNAPSHOTS {chartDataPayload.length > 0 && `· DBA ${chartDataPayload[0]?.dba} → ${chartDataPayload[chartDataPayload.length - 1]?.dba}`}
              </div>
            </div>
            <div className="groupiq-snapshot-description">
              SOURCE ROWS · AS-OF DATE · GROUP BLOCK · GROUP CONFIRMED · GROUP WASH · PERIOD-OVER-PERIOD DELTAS
            </div>
            
            <div className="groupiq-table-wrapper">
              <table className="groupiq-table">
                <thead>
                  <tr>
                    <th className="border-right" >AS-OF DATE</th>
                    <th className="border-right text-center">DBA</th>
                    <th className="border-right text-center">GROUP BLOCK</th>
                    <th className="border-right text-center">GROUP CONFIRMED</th>
                    <th className="border-right text-center">GROUP WASH</th>
                    <th className="border-right text-center">GROUP WASH %</th>
                    <th className="border-right text-end groupiq-table-header-delta">Δ GROUP BLOCK</th>
                    <th className="border-right text-end groupiq-table-header-delta">Δ GROUP CONFIRMED</th>
                  </tr>
                </thead>
                <tbody>
                  {chartDataPayload.map((row, i) => {
                    const isEven = i % 2 === 0;
                    const washPct = parseFloat(String(row.groupwashpct || row["groupwash%"] || "0").replace('%', ''));
                    const washColor = getColorByWash(row);
                    
                    const formatChange = (val) => {
                      if (val === null || val === undefined || val === 0) return <span className="groupiq-change-none">-</span>;
                      if (val > 0) return <span className="groupiq-change-positive">+{val}</span>;
                      return <span className="groupiq-change-negative">{val}</span>;
                    };

                    return (
                      <tr key={i} className={isEven ? "groupiq-table-row-even" : "groupiq-table-row-odd"}>
                        <td className="groupiq-table-date border-right">
                          {row.asofdate ? moment(row.asofdate).format("MMM DD, YYYY") : "-"}
                        </td>
                        <td className="border-right groupiq-table-numeric">{row.dba !== undefined ? row.dba : "-"}</td>
                        <td className="border-right groupiq-table-numeric">{row.groupblock || 0}</td>
                        <td className="border-right groupiq-table-numeric">{row.groupconfirm || 0}</td>
                        <td className="border-right groupiq-table-numeric">{row.groupwash || 0}</td>
                        <td className="border-right">
                          <div className="groupiq-table-wash-progress justify-content-center">
                            <div className="groupiq-wash-bar">
                              <div className="groupiq-wash-bar-fill" style={{ "--w": `${Math.min(washPct, 100)}%`, "--accent": washColor }}></div>
                            </div>
                            <span className="groupiq-wash-percentage" style={{ "--accent": washColor }}>{Math.round(washPct)}%</span>
                          </div>
                        </td>
                        <td className="border-right groupiq-table-delta">
                          {formatChange(row.groupblockchange, "#d97706")}
                        </td>
                        <td className="border-right groupiq-table-delta">
                          {formatChange(row.groupconfirmchange, "#22c55e")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupIQ;
